import React, { FunctionComponent, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/reducers/index";
import {
  setMarcaBlancaSellerCode,
  clearMarcaBlancaSellerCode,
  setMarcaBlancaBranch,
} from "../../redux/actions/marcaBlancaActions";
import {
  ReturnLinkContainer,
  AddresInfoContainer,
} from "../../views/CartSummary/style";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import {
  CaretDownIcon,
  CheckCircleIcon,
  PencilSimpleIcon,
  UserIcon,
} from "@phosphor-icons/react";
import { language } from "../../utils/language";
import config from "../../config/enviroment";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import useSellerCodeValidation from "../../hooks/useSellerCodeValidation";
import {
  sanitizeSellerCode,
  isValidSellerCodeFormat,
} from "../../utils/sellerCodeValidation";
import { getCartUnits } from "../../utils/marcaBlancaCart";
import MarcaBlancaBranchAvailabilityModal from "../MarcaBlancaBranchAvailabilityModal";
import {
  Container,
  PageTitle,
  StepBlock,
  StepHeader,
  StepCircle,
  StepLabel,
  Hint,
  HintLink,
  EmailBox,
  EmailIcon,
  EmailText,
  BranchSelectWrap,
  BranchTrigger,
  BranchOptions,
  BranchOption,
  BranchOptionName,
  BranchOptionCount,
  SummaryBox,
  SummaryTitle,
  SummaryStats,
  SummaryStat,
  AddressFieldsBlock,
  AddressRow,
  AddressField,
  AddressDetailLabel,
  AddressDetailBox,
  SellerCodeRow,
  SellerCodeInput,
  SellerCodeViewRow,
  SellerCodeViewLeft,
  SellerCodeViewText,
  SellerCodeEditButton,
  SellerCodeError,
  ValidateButton,
  Footer,
  ContinueButton,
} from "./styles";

type MarcaBlancaAddressInfoProps = {
  color: string;
  register: any;
  setValue: any;
  getFormValues: any;
  nextStep: () => void;
  previousStep: () => void;
  showModal: boolean;
  goToCart: () => void;
};

const MarcaBlancaAddressInfo: FunctionComponent<MarcaBlancaAddressInfoProps> = ({
  color,
  register,
  setValue,
  nextStep,
  previousStep,
  showModal,
  goToCart,
}) => {
  const dispatch = useDispatch();
  const homeState: any = useSelector((state: RootState) => state.home);
  const marcaBlancaState: any = useSelector(
    (state: RootState) => state.marcaBlanca
  );
  const shoppingCartState: any = useSelector(
    (state: RootState) => state.shoppingCart
  );
  const branches = marcaBlancaState.client?.branches ?? [];
  const email = marcaBlancaState.client?.email ?? "";
  const lang = language[homeState.default_language];

  const { validateSellerCode, loading: sellerCodeLoading } =
    useSellerCodeValidation(marcaBlancaState.clientId);

  const [selectedBranchId, setSelectedBranchId] = useState(
    marcaBlancaState.branch?.id ?? ""
  );
  const [isBranchListOpen, setIsBranchListOpen] = useState(false);
  const [sellerCode, setSellerCode] = useState(marcaBlancaState.sellerCode);
  const [sellerCodeValidated, setSellerCodeValidated] = useState(
    marcaBlancaState.sellerCodeValidated
  );
  const [sellerCodeRejected, setSellerCodeRejected] = useState(false);
  const [isEditingSellerCode, setIsEditingSellerCode] = useState(
    !marcaBlancaState.sellerCodeValidated
  );
  const [branchAvailabilityModal, setBranchAvailabilityModal] = useState<
    "unavailable" | "exceeds" | null
  >(null);

  const branchListRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(branchListRef, () => setIsBranchListOpen(false));

  const selectedBranch = branches.find(
    (branch: any) => branch.id === selectedBranchId
  );

  const validateBranchAvailability = (branch: any): boolean => {
    if (!branch) return true;
    const available = branch.dataphonesAvailable ?? 0;
    const cartUnits = getCartUnits(shoppingCartState.cartInfo);
    if (available === 0) {
      setBranchAvailabilityModal("unavailable");
      return false;
    }
    if (cartUnits > available) {
      setBranchAvailabilityModal("exceeds");
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (marcaBlancaState.branch?.id) {
      setValue("branchId", marcaBlancaState.branch.id);
    }
    if (marcaBlancaState.sellerCodeValidated) {
      setValue("sellerCode", marcaBlancaState.sellerCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasSingleBranch = branches.length === 1;

  useEffect(() => {
    if (!selectedBranchId && hasSingleBranch) {
      setSelectedBranchId(branches[0].id);
      setValue("branchId", branches[0].id);
      dispatch(setMarcaBlancaBranch(branches[0]));
      validateBranchAvailability(branches[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branches]);

  const handleBranchSelect = (branchId: string) => {
    const branch =
      branches.find((branch: any) => branch.id === branchId) ?? null;
    setSelectedBranchId(branchId);
    setValue("branchId", branchId);
    dispatch(setMarcaBlancaBranch(branch));
    setIsBranchListOpen(false);
    validateBranchAvailability(branch);
  };

  const handleSellerCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = sanitizeSellerCode(e.target.value);
    setSellerCode(value);
    setSellerCodeValidated(false);
    setSellerCodeRejected(false);
    setValue("sellerCode", "");
    dispatch(clearMarcaBlancaSellerCode());
  };

  const handleValidateSellerCode = async () => {
    if (!isValidSellerCodeFormat(sellerCode)) {
      setSellerCodeValidated(false);
      setSellerCodeRejected(true);
      return;
    }
    const { valid } = await validateSellerCode(sellerCode);
    if (valid) {
      setSellerCodeValidated(true);
      setSellerCodeRejected(false);
      setIsEditingSellerCode(false);
      setValue("sellerCode", sellerCode);
      dispatch(setMarcaBlancaSellerCode(sellerCode));
    } else {
      setSellerCodeValidated(false);
      setSellerCodeRejected(true);
      setValue("sellerCode", "");
      dispatch(clearMarcaBlancaSellerCode());
    }
  };

  const handleEditSellerCode = () => {
    setIsEditingSellerCode(true);
    setSellerCodeRejected(false);
  };

  const handleContinueClick = () => {
    if (!validateBranchAvailability(selectedBranch)) return;
    nextStep();
  };

  return (
    <AddresInfoContainer showModal={showModal}>
      <Container>
        <input type="hidden" name="branchId" ref={register()} />
        <input type="hidden" name="sellerCode" ref={register()} />

        <PageTitle>{lang["marcaBlancaAddressInfoTitle"]}</PageTitle>

        <StepBlock>
          <StepHeader>
            <StepCircle>1</StepCircle>
            <StepLabel>{lang["marcaBlancaAccountStepLabel"]}</StepLabel>
          </StepHeader>
          <EmailBox>
            <EmailIcon>
              <UserIcon width={18} height={20} color="#ffffff" weight="fill" />
            </EmailIcon>
            <EmailText>{email}</EmailText>
          </EmailBox>
        </StepBlock>

        <StepBlock>
          <StepHeader>
            <StepCircle>2</StepCircle>
            <StepLabel>
              {selectedBranch
                ? lang["marcaBlancaBranchDataStepLabel"]
                : lang["marcaBlancaBranchStepLabel"]}
            </StepLabel>
          </StepHeader>
          <Hint>
            {lang["marcaBlancaBranchHintBefore"]}
            <HintLink
              onClick={() =>
                window.open(
                  config.epaycoDashboardLoginUrl,
                  "_blank",
                  "noopener,noreferrer"
                )
              }
            >
              {lang["marcaBlancaBranchHintLink"]}
            </HintLink>
            {lang["marcaBlancaBranchHintAfter"]}
          </Hint>
          <BranchSelectWrap ref={branchListRef}>
            <BranchTrigger
              type="button"
              hasValue={!!selectedBranch}
              disabled={hasSingleBranch}
              onClick={() => setIsBranchListOpen((open) => !open)}
            >
              <span>
                {selectedBranch
                  ? selectedBranch.name
                  : lang["marcaBlancaBranchPlaceholder"]}
              </span>
              {!hasSingleBranch && (
                <CaretDownIcon
                  size={16}
                  color="#000000"
                  style={{
                    transform: isBranchListOpen
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                  }}
                />
              )}
            </BranchTrigger>
            {isBranchListOpen && (
              <BranchOptions>
                {branches.map((branch: any) => {
                  const acquired = branch.dataphonesAcquired ?? 0;
                  const max = acquired + (branch.dataphonesAvailable ?? 0);
                  const linkedCountText = lang["marcaBlancaBranchLinkedCount"]
                    .replace("{count}", String(acquired))
                    .replace("{max}", String(max));
                  return (
                    <BranchOption
                      key={branch.id}
                      onClick={() => handleBranchSelect(branch.id)}
                    >
                      <BranchOptionName>{branch.name}</BranchOptionName>
                      <BranchOptionCount>{linkedCountText}</BranchOptionCount>
                    </BranchOption>
                  );
                })}
              </BranchOptions>
            )}
          </BranchSelectWrap>
          {selectedBranch && (
            <SummaryBox>
              <SummaryTitle>{lang["marcaBlancaBranchSummaryTitle"]}</SummaryTitle>
              <SummaryStats>
                <SummaryStat>
                  {lang["marcaBlancaBranchLinked"].replace(
                    "{count}",
                    String(selectedBranch.dataphonesAcquired ?? 0)
                  )}
                </SummaryStat>
                <SummaryStat>
                  {lang["marcaBlancaBranchAvailable"].replace(
                    "{count}",
                    String(selectedBranch.dataphonesAvailable ?? 0)
                  )}
                </SummaryStat>
              </SummaryStats>
            </SummaryBox>
          )}
        </StepBlock>

        {selectedBranch && (
          <AddressFieldsBlock>
            <AddressField readOnly value="Colombia" />
            <AddressRow>
              <AddressField readOnly value={selectedBranch.regionName} />
              <AddressField readOnly value={selectedBranch.cityName} />
            </AddressRow>
            <AddressField readOnly value={selectedBranch.formatted} />
            <AddressDetailLabel>
              {lang["marcaBlancaAddressDetailLabel"]}
            </AddressDetailLabel>
            <AddressDetailBox>
              {selectedBranch.addressDetail}
            </AddressDetailBox>
          </AddressFieldsBlock>
        )}

        <StepBlock>
          <StepHeader>
            <StepCircle>3</StepCircle>
            <StepLabel>{lang["marcaBlancaSellerCodeStepLabel"]}</StepLabel>
          </StepHeader>
          {isEditingSellerCode ? (
            <>
              <SellerCodeRow>
                <SellerCodeInput
                  type="text"
                  value={sellerCode}
                  onChange={handleSellerCodeChange}
                  placeholder={lang["marcaBlancaSellerCodePlaceholder"]}
                />
                <ValidateButton
                  type="button"
                  disabled={sellerCode.trim() === "" || sellerCodeLoading}
                  onClick={handleValidateSellerCode}
                >
                  {lang["marcaBlancaSellerCodeValidate"]}
                </ValidateButton>
              </SellerCodeRow>
              {sellerCodeRejected && (
                <SellerCodeError>
                  {lang["marcaBlancaSellerCodeRejected"]}
                </SellerCodeError>
              )}
            </>
          ) : (
            <SellerCodeViewRow>
              <SellerCodeViewLeft>
                <CheckCircleIcon size={32} color="#67c940" weight="fill" />
                <SellerCodeViewText>{sellerCode}</SellerCodeViewText>
              </SellerCodeViewLeft>
              <SellerCodeEditButton
                type="button"
                onClick={handleEditSellerCode}
              >
                <PencilSimpleIcon size={24} color="#e1251b" />
              </SellerCodeEditButton>
            </SellerCodeViewRow>
          )}
        </StepBlock>

        <Footer>
          <ReturnLinkContainer className="return-link-container">
            <a
              style={{ cursor: "pointer", color: `${color}` }}
              onClick={() => previousStep()}
              className="return-link"
            >
              <ChevronLeftIcon style={{ color: `${color}` }} />{" "}
              {lang["back"]}
            </a>
          </ReturnLinkContainer>
          <ContinueButton
            color={color}
            loading={false}
            onClick={handleContinueClick}
            disabled={!selectedBranchId}
          >
            {lang["continue"]}
          </ContinueButton>
        </Footer>

        {branchAvailabilityModal === "unavailable" && (
          <MarcaBlancaBranchAvailabilityModal
            open
            color={color}
            title={lang["marcaBlancaBranchUnavailableTitle"]}
            message={lang["marcaBlancaBranchUnavailableMessage"]}
            primaryLabel={lang["marcaBlancaBranchUnavailablePrimary"]}
            secondaryLabel={lang["marcaBlancaBranchUnavailableSecondary"]}
            onPrimary={() => setBranchAvailabilityModal(null)}
            onSecondary={() => setBranchAvailabilityModal(null)}
            onClose={() => setBranchAvailabilityModal(null)}
          />
        )}
        {branchAvailabilityModal === "exceeds" && (
          <MarcaBlancaBranchAvailabilityModal
            open
            color={color}
            title={lang["marcaBlancaBranchExceedsTitle"]}
            message={lang["marcaBlancaBranchExceedsMessage"]}
            primaryLabel={lang["marcaBlancaBranchExceedsPrimary"]}
            secondaryLabel={lang["marcaBlancaBranchExceedsSecondary"]}
            onPrimary={() => {
              setBranchAvailabilityModal(null);
              goToCart();
            }}
            onSecondary={() => setBranchAvailabilityModal(null)}
            onClose={() => setBranchAvailabilityModal(null)}
          />
        )}
      </Container>
    </AddresInfoContainer>
  );
};

export default MarcaBlancaAddressInfo;
